import * as React from "react";
import { Selection, type IObjectWithKey, type IColumn, Stack } from "@fluentui/react";
import { omit } from "ramda";
import { action, computed, makeObservable, observable } from "mobx";
import {
    type VehiclesApiResource,
    type VehiclesQuery,
} from "../../domain/repositories/repository-vehicles";
import { createUuid, type UUID } from "../../utils/uuid";
import {
    type VehicleSortKey,
    type ColumnVisibility,
    type Vehicle,
    VehiclesExportVehicleListListType,
} from "../../api";
import { observer } from "mobx-react";
import { ListVehicleItemCell } from "../atoms/list-vehicle-item-cell";
import type { SortState } from "../../utils/sort-state";
import type { PaginationState } from "../../utils/pagination-state";
import { LayoutList } from "../layouts/layout-list";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import { ListSearchField } from "../atoms/list-search-field";
import type { SelectionState } from "../../utils/selection-state";
import type { SearchState } from "../../utils/search-state";
import { FilterVehicleList, type FilterVehicleListQuery } from "../organisms/filter-vehicle-list";
import { ExportButton, type ExportQuery } from "./export-button";
import { defaultPageSize } from "../../utils/constants";

export enum ListVehiclesLayout {
    Default,
    FleetStatistics,
    Maintenance,
    Group,
}

export interface ListVehiclesProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly includeInactive?: boolean;
    readonly vehicleGroupIds?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly ignoreColumnVisibility?: boolean;
    readonly listStateId?: UUID;
    readonly columnLayout?: ListVehiclesLayout;
    readonly hideExport?: boolean;
    readonly hideFilter?: boolean;
    readonly ignoreUrl?: boolean;
}

interface ListVehicleItem extends IObjectWithKey {
    readonly key: UUID;
}

import { store } from "../../store";

@observer
export class ListVehicles extends React.Component<ListVehiclesProps> {
    private _listStateId = createUuid();
    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    @observable private accessor filterVehicleListQuery: FilterVehicleListQuery = {
        filteredDepartments: [],
        filteredVehicleGroups: [],
        filteredProcurementTypes: [],
        isFiltered: false,
    };

    constructor(props: ListVehiclesProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        if (!this.props) {
            return;
        }
        store.serviceListStates.vehicles.initializeList(this.listStateId, {
            query: () => this.query,
            ignoreUrl: this.props.ignoreUrl ?? false,
        });
    }

    @computed private get columnVisibility(): ColumnVisibility | undefined {
        return store.repositoryColumnVisibility.byQuery()[0];
    }

    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<VehicleSortKey> {
        return store.serviceListStates.vehicles.getSortState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<VehiclesApiResource> {
        return store.serviceListStates.vehicles.getPaginationState(this.listStateId);
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.vehicles.getSelectionState(this.listStateId);
    }

    @computed private get searchState(): SearchState {
        return store.serviceListStates.vehicles.getSearchState(this.listStateId);
    }

    @computed private get query(): VehiclesQuery {
        const query = {
            pageSize: defaultPageSize,
            includeInactive: this.props.includeInactive,
            ...this.sortState.query,
            ...this.searchState.query,
        } as VehiclesQuery;
        if (this.filterVehicleListQuery === undefined) {
            return query;
        }
        const filteredDepartments = this.filterVehicleListQuery.filteredDepartments;
        if (filteredDepartments && filteredDepartments.length > 0) {
            query.departmentIds = filteredDepartments;
        }
        const filteredVehicleGroups = this.filterVehicleListQuery.filteredVehicleGroups;
        if (filteredVehicleGroups && filteredVehicleGroups.length > 0) {
            query.vehicleGroupIds = filteredVehicleGroups;
        }
        const filteredProcurementTypes = this.filterVehicleListQuery.filteredProcurementTypes;
        if (filteredProcurementTypes && filteredProcurementTypes.length > 0) {
            query.procurementTypes = filteredProcurementTypes;
        }
        if (this.props.vehicleGroupIds) {
            query.vehicleGroupIds = (query.vehicleGroupIds ?? []).concat(
                this.props.vehicleGroupIds,
            );
        }
        return query;
    }

    @computed private get exportQuery(): ExportQuery {
        const query = Object.entries({
            list_type: VehiclesExportVehicleListListType.TRUCKS,
            ...this.query,
        });
        return { query, basePath: "/api/v1/vehicles/export?" };
    }

    @computed private get vehicles(): (Vehicle | undefined)[] {
        if (this.props.ids) {
            return this.props.ids.map((id) => store.repositoryVehicles.byId(id));
        }
        return store.repositoryVehicles.byQuery(this.query, this.paginationState.pagination);
    }

    @computed private get items(): (ListVehicleItem | undefined)[] {
        return this.vehicles.map((vehicle) => {
            if (!vehicle) {
                return;
            }
            return {
                key: vehicle.id,
            };
        });
    }

    @computed private get fleetStatisticsColumn(): IColumn[] {
        const columns: IColumn[] = [];

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.fleetStatisticsList.vehicleManufacturer
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.manufacturer.name"),
                key: "manufacturer",
                minWidth: 100,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.fleetStatisticsList.vehicleSerialNumber
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.serialNumber.name"),
                key: "serialNumber",
                minWidth: 120,
                maxWidth: 200,
            });
        columns.push({
            name: store.i18n.t("listVehicles.column.procurementType.name"),
            key: "procurementType",
            minWidth: 150,
            maxWidth: 200,
        });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehiclePurchaseCost
        ) {
            columns.push({
                name: store.i18n.t("listVehicles.column.purchaseCost.name"),
                key: "purchaseCost",
                minWidth: 150,
                maxWidth: 200,
            });
        }
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleMonthlyCost
        ) {
            columns.push({
                name: store.i18n.t("listVehicles.column.monthlyCost.name"),
                key: "monthlyCost",
                minWidth: 150,
                maxWidth: 200,
            });
        }
        columns.push({
            name: store.i18n.t("listVehicles.column.purchaseTermBegin.name"),
            key: "purchaseTermBegin",
            minWidth: 150,
            maxWidth: 200,
        });
        columns.push({
            name: store.i18n.t("listVehicles.column.contractTermEnd.name"),
            key: "contractTermEnd",
            minWidth: 150,
            maxWidth: 200,
        });
        columns.push({
            name: store.i18n.t("listVehicles.column.department.name"),
            key: "department",
            minWidth: 150,
            maxWidth: 200,
        });
        columns.push({
            name: store.i18n.t("listVehicles.column.contractFile.name"),
            key: "contractFile",
            minWidth: 150,
            maxWidth: 200,
        });
        return columns;
    }

    // This column layout is shown in the detail panels of VehicleGroups.
    @computed private get groupColumns(): IColumn[] {
        const columns: IColumn[] = [];

        columns.push({
            name: store.i18n.t("listVehicles.column.serialNumber.name"),
            key: "serialNumber",
            minWidth: 120,
            maxWidth: 200,
        });
        columns.push({
            name: store.i18n.t("listVehicles.column.manufacturer.name"),
            key: "manufacturer",
            minWidth: 100,
            maxWidth: 200,
        });
        columns.push({
            name: store.i18n.t("listVehicles.column.model.name"),
            key: "model",
            minWidth: 100,
            maxWidth: 200,
        });
        columns.push({
            name: store.i18n.t("listVehicles.column.vehicleType.name"),
            key: "vehicleType",
            minWidth: 100,
            maxWidth: 200,
        });
        columns.push({
            name: store.i18n.t("listVehicles.column.department.name"),
            key: "department",
            minWidth: 150,
            maxWidth: 200,
        });
        columns.push({
            name: store.i18n.t("listVehicles.column.vehicleStatus.name"),
            key: "vehicleStatus",
            minWidth: 150,
            maxWidth: 200,
        });

        return columns;
    }

    @computed private get maintenanceColumns(): IColumn[] {
        const columns: IColumn[] = [];

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleStatus
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.vehicleStatus.name"),
                key: "vehicleStatus",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleSerialNumber
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.serialNumber.name"),
                key: "serialNumber",
                minWidth: 120,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleManufacturer
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.manufacturer.name"),
                key: "manufacturer",
                minWidth: 100,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleModel
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.model.name"),
                key: "model",
                minWidth: 100,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleTypeLabel
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.vehicleType.name"),
                key: "vehicleType",
                minWidth: 100,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleEngineType
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.engineType.name"),
                key: "engineType",
                minWidth: 100,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleManufactureYear
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.manufactureYear.name"),
                key: "manufactureYear",
                minWidth: 80,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.departmentLabel
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.department.name"),
                key: "department",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehiclePurchaseDateOrContractTermStart
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.purchaseTermBegin.name"),
                key: "purchaseTermBegin",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleContractTermEnd
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.contractTermEnd.name"),
                key: "contractTermEnd",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleContractDaysRemaining
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.daysUntilContractTermEnd.name"),
                key: "daysUntilContractTermEnd",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleInitialOperatingHours
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.initialOperatingHours.name"),
                key: "initialOperatingHours",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleCurrentOperatingHours
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.currentOperatingHours.name"),
                key: "currentOperatingHours",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleLastMaintenanceOperatingHours
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.lastMaintenanceHours.name"),
                key: "lastMaintenanceHours",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleLastMaintenanceDate
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.lastMaintenanceDate.name"),
                key: "lastMaintenanceDate",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleNextMaintenanceHours
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.nextMaintenanceHours.name"),
                key: "nextMaintenanceHours",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleMaintenanceInterval
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.maintenanceInterval.name"),
                key: "maintenanceInterval",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleMaintenanceNotificationAt
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.maintenanceNotificationAt.name"),
                key: "maintenanceNotificationAt",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleMaintenanceLockdown
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.maintenanceLockdown.name"),
                key: "maintenanceLockdown",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.maintenanceList.vehicleHoursUntilNextMaintenance
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.hoursUntilMaintenance.name"),
                key: "hoursUntilMaintenance",
                minWidth: 150,
                maxWidth: 200,
            });

        return columns;
    }

    @computed private get defaultColumns(): IColumn[] {
        const columns: IColumn[] = [];

        if (this.props.ignoreColumnVisibility || this.columnVisibility?.vehicleList.vehicleStatus)
            columns.push({
                name: store.i18n.t("listVehicles.column.vehicleStatus.name"),
                key: "vehicleStatus",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleSerialNumber
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.serialNumber.name"),
                key: "serialNumber",
                minWidth: 120,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehiclePhoneImei
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.phoneImei.name"),
                key: "phoneImei",
                minWidth: 100,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleDeviceSerialNumber
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.eloboxId.name"),
                key: "eloboxId",
                minWidth: 100,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleManufacturer
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.manufacturer.name"),
                key: "manufacturer",
                minWidth: 100,
                maxWidth: 200,
            });
        if (this.props.ignoreColumnVisibility || this.columnVisibility?.vehicleList.vehicleModel)
            columns.push({
                name: store.i18n.t("listVehicles.column.model.name"),
                key: "model",
                minWidth: 100,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleTypeLabel
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.vehicleType.name"),
                key: "vehicleType",
                minWidth: 100,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleEngineType
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.vehicleEngineType.name"),
                key: "engineType",
                minWidth: 100,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleManufactureYear
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.manufactureYear.name"),
                key: "manufactureYear",
                minWidth: 80,
                maxWidth: 200,
            });
        if (this.props.ignoreColumnVisibility || this.columnVisibility?.vehicleList.departmentLabel)
            columns.push({
                name: store.i18n.t("listVehicles.column.department.name"),
                key: "department",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleGroupLabel
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.group.name"),
                key: "group",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleShockProfile
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.shockProfile.name"),
                key: "shockProfile",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehiclePreOpsChecklist
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.checklistProfile.name"),
                key: "checklistProfile",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleChecklistOverride
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.checklistOverride.name"),
                key: "checklistOverride",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleIdleAutoLogoutAfter
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.vehicleIdleAutoLogoutAfter.name"),
                key: "vehicleIdleAutoLogoutAfter",
                minWidth: 150,
                maxWidth: 200,
            });
        if (this.props.ignoreColumnVisibility || this.columnVisibility?.vehicleList.userFullName)
            columns.push({
                name: store.i18n.t("listVehicles.column.lastDriver.name"),
                key: "lastDriver",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleLastSynchronizedAt
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.lastSynchronization.name"),
                key: "lastSynchronization",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehiclePurchaseDateOrContractTermStart
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.purchaseTermBegin.name"),
                key: "purchaseTermBegin",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleContractTermEnd
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.contractTermEnd.name"),
                key: "contractTermEnd",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleContractDaysRemaining
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.daysUntilContractTermEnd.name"),
                key: "daysUntilContractTermEnd",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehiclePurchaseCost
        ) {
            columns.push({
                name: store.i18n.t("listVehicles.column.purchaseCost.name"),
                key: "purchaseCost",
                minWidth: 150,
                maxWidth: 200,
            });
        }
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleMonthlyCost
        ) {
            columns.push({
                name: store.i18n.t("listVehicles.column.monthlyCost.name"),
                key: "monthlyCost",
                minWidth: 150,
                maxWidth: 200,
            });
        }
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleInitialOperatingHours
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.initialOperatingHours.name"),
                key: "initialOperatingHours",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleCurrentOperatingHours
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.currentOperatingHours.name"),
                key: "currentOperatingHours",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleLastMaintenanceOperatingHours
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.lastMaintenanceHours.name"),
                key: "lastMaintenanceHours",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleLastMaintenanceDate
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.lastMaintenanceDate.name"),
                key: "lastMaintenanceDate",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleNextMaintenanceHours
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.nextMaintenanceHours.name"),
                key: "nextMaintenanceHours",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleMaintenanceInterval
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.maintenanceInterval.name"),
                key: "maintenanceInterval",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleMaintenanceNotificationAt
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.maintenanceNotificationAt.name"),
                key: "maintenanceNotificationAt",
                minWidth: 150,
                maxWidth: 200,
            });
        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleMaintenanceLockdown
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.maintenanceLockdown.name"),
                key: "maintenanceLockdown",
                minWidth: 150,
                maxWidth: 200,
            });

        if (
            this.props.ignoreColumnVisibility ||
            this.columnVisibility?.vehicleList.vehicleHoursUntilNextMaintenance
        )
            columns.push({
                name: store.i18n.t("listVehicles.column.hoursUntilMaintenance.name"),
                key: "hoursUntilMaintenance",
                minWidth: 150,
                maxWidth: 200,
            });

        return columns;
    }

    @computed private get columns(): IColumn[] {
        if (this.props.columnLayout === ListVehiclesLayout.FleetStatistics) {
            return this.fleetStatisticsColumn;
        } else if (this.props.columnLayout === ListVehiclesLayout.Maintenance) {
            return this.maintenanceColumns;
        } else if (this.props.columnLayout === ListVehiclesLayout.Group) {
            return this.groupColumns;
        } else {
            return this.defaultColumns;
        }
    }

    private renderItem(vehicleId: UUID, column: IColumn): JSX.Element {
        return (
            <ListVehicleItemCell
                vehicleId={vehicleId}
                column={column}
                filteredVehicleGroups={this.filterVehicleListQuery.filteredVehicleGroups ?? []}
            />
        );
    }

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    @action.bound private setVehicleListFilter(filterQuery: FilterVehicleListQuery): void {
        this.filterVehicleListQuery = {
            filteredDepartments: filterQuery.filteredDepartments,
            filteredVehicleGroups: filterQuery.filteredVehicleGroups,
            filteredProcurementTypes: filterQuery.filteredProcurementTypes,
            isFiltered: filterQuery.isFiltered,
        };
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.vehicles.isInitialized(this.listStateId)) {
            return <></>;
        }

        // In the group details panel we always show the full list of vehicles
        // and so we disable the search functionality.
        let searchField;
        if (this.props.columnLayout !== ListVehiclesLayout.Group) {
            searchField = (
                <ListSearchField
                    placeholder={store.i18n.t("listVehicles.search.placeholder")}
                    listStateId={this.listStateId}
                    listState={store.serviceListStates.vehicles}
                />
            );
        }

        let filter;
        if (!this.props.hideFilter) {
            filter = <FilterVehicleList setVehicleListFilter={this.setVehicleListFilter} />;
        }

        // Forward properties that are used by Fluent UI to the `ShimmeredDetailsList`.
        const props = omit(["ids"], this.props);
        return (
            <LayoutList
                paginationState={this.paginationState}
                commandBar={
                    <Stack horizontal horizontalAlign="space-between">
                        {this.props.commandBar}
                        {!this.props.hideExport && <ExportButton query={this.exportQuery} />}
                    </Stack>
                }
                searchField={searchField}
                filter={filter}
            >
                <ElofleetList
                    {...props}
                    selection={this.selection}
                    onRenderItemColumn={(item: ListVehicleItem, _index, column) =>
                        this.renderItem(item.key, column!)
                    }
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    columns={this.sortState.patchColumns(this.columns)}
                    items={this.items}
                />
            </LayoutList>
        );
    }
}
